<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import V2ReservationsNotice from '../V2ReservationsNotice.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import {
    canSelectReservationDepositScope,
    reservationDepositRequired,
    reservationDepositScope,
  } from '@/domain/reservations/reservation-deposit-scope';
  import type { IReservationDepositScopeSettings } from '@/domain/reservations/reservation-deposit-scope';

  const props = withDefaults(
    defineProps<{
      value: boolean | null;
      label: string;
      /** Saved configuration.settings, never the unsaved General settings draft. */
      settings: IReservationDepositScopeSettings;
      disabled?: boolean;
    }>(),
    { disabled: false }
  );
  const emit = defineEmits<{
    (e: 'input', value: boolean | null): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const controlDisabled = computed(
    () => props.disabled || !props.settings.mpConnected
  );
  const scope = computed(() => reservationDepositScope(props.value));
  const items = computed(() =>
    ['inherit', 'always', 'never'].map((id) => ({
      id,
      label: String(proxy.$t(`reservations.depositScope.${id}`)),
      disabled: !canSelectReservationDepositScope(id, props.settings),
    }))
  );
  const needsDepositSettings = computed(
    () =>
      props.settings.mpConnected &&
      !canSelectReservationDepositScope('always', props.settings)
  );
  function select(value: string) {
    if (
      !canSelectReservationDepositScope(value, props.settings, props.disabled)
    )
      return;
    const depositRequired = reservationDepositRequired(value);
    if (depositRequired !== undefined) emit('input', depositRequired);
  }
</script>

<template>
  <fieldset class="deposit-scope-field" :disabled="controlDisabled">
    <legend>{{ label }}</legend>
    <PosUiSegmented
      :value="scope"
      :items="items"
      :disabled="controlDisabled"
      :aria-label="label"
      block
      @input="select"
    />
    <p v-if="needsDepositSettings" class="hint">
      {{ $t('reservations.depositScope.hint') }}
    </p>
    <V2ReservationsNotice
      v-if="!settings.mpConnected"
      class="connection-notice"
      icon="credit-card-outline"
      :text="$t('reservations.depositSettings.needsMp')"
    />
  </fieldset>
</template>

<style scoped lang="scss">
  .deposit-scope-field {
    @apply tw-m-0 tw-min-w-0 tw-border-0 tw-p-0;

    legend {
      @apply tw-mb-2 tw-p-0 tw-text-sm tw-font-medium tw-text-neutral-800;
    }

    .hint {
      @apply tw-mb-0 tw-mt-2 tw-text-xs tw-text-neutral-500;
    }

    .connection-notice {
      @apply tw-mb-0 tw-mt-3;
    }

    :deep(.pos-ui-segmented-option) {
      @apply tw-h-auto tw-min-h-11 tw-min-w-0 tw-whitespace-normal tw-px-2 tw-py-2;
    }
  }
</style>
